const {
  PLAN_CAPABILITIES,
  getPlanCapabilities,
  hasPlanCapability,
  plansWithCapability,
  isOpsPlan,
  isFoodMoverLitePlan,
  HOME_DELIVERY_ADDON_ELIGIBLE_PLANS,
  HOME_DELIVERY_ADDON_PLAN_MAP,
  HOME_DELIVERY_ADDON_BASE_PLAN_MAP,
  getHomeDeliveryAddonTargetPlan,
  getHomeDeliveryAddonBasePlan,
  canAddHomeDeliveryAddon,
  isHomeDeliveryAddonPlan,
  normalizePlanKey,
  hasEffectiveHomeDelivery,
  getEffectivePlanCapabilities,
} = require('./planCapabilities');
const {
  PLAN_PRICING,
  HOME_DELIVERY_ADDON_PRICING,
  WHITE_LABEL_PRICING,
  getPlanPricing,
  formatMoney,
  formatPlanPriceSummary,
} = require('./planPricing');

/**
 * Stripe product/price map.
 *
 * priceIds[0] is the default for new checkouts / artificial grants.
 * Additional priceIds keep legacy subscribers mapped until renewal.
 * Placeholder IDs (notlive_*) are swapped when live Stripe prices exist.
 */
const subscriptions = {
  basic: {
    test: {
      priceIds: ['price_1J2JwiHRNVAUEyHHDhDHJhvZ'],
      productId: 'prod_JffHjUeP5IbCnA',
    },
    live: {
      priceIds: ['price_1JCdyUHRNVAUEyHHQFp7SDwc'],
      productId: 'prod_JqKdfYcbSBwoyf',
    },
  },
  foodMoverLite: {
    test: {
      // Working Stripe prices first so checkout still works; new
      // monthly/annual IDs prepended when created (existing subs stay on
      // their price until renewal).
      priceIds: [
        'price_1ThckiHRNVAUEyHHUbzze39O',
        'notlive_food_mover_lite_monthly',
        'notlive_food_mover_lite_annual',
      ],
      productId: 'prod_Uh0m3JfocyEJmO',
    },
    live: {
      priceIds: [
        'price_1TjhseHRNVAUEyHHTewIdsE0',
        'notlive_food_mover_lite_monthly',
        'notlive_food_mover_lite_annual',
      ],
      productId: 'prod_JqKdvorQA531Jd',
    },
  },
  // Food Mover Pro — same product ID so existing orgs keep mapping
  foodMover: {
    test: {
      priceIds: [
        'price_1TjvCPHRNVAUEyHHL38I5nU9',
        'price_1J2JxOHRNVAUEyHH4krNi4Mc',
        'notlive_food_mover_pro_monthly',
        'notlive_food_mover_pro_annual',
      ],
      productId: 'prod_JffHHOgbk7XLlQ',
    },
    live: {
      priceIds: [
        'price_1TjhulHRNVAUEyHHSb6tD5b4',
        'price_1JCdybHRNVAUEyHHHX63HPS3',
        'notlive_food_mover_pro_monthly',
        'notlive_food_mover_pro_annual',
      ],
      productId: 'prod_JqKdmYHItQwM5L',
    },
  },
  foodDeliveryPro: {
    test: {
      // $250/mo — Food Delivery Pro (Distributions / home delivery only)
      priceIds: ['price_1TwHvWHRNVAUEyHHWnjwg1Ta'],
      productId: 'prod_UwAGqtcrcGjpkX',
    },
    live: {
      // $250/mo — Food Delivery Pro (Distributions / home delivery only)
      priceIds: ['price_1TwHlvHRNVAUEyHH8DrbEhYs'],
      productId: 'prod_UwA6rf2v775CyT',
    },
  },
  // Legacy bundle SKUs (product-id mapping only — prefer base plan + HD add-on)
  foodMoverLiteHome: {
    test: {
      priceIds: ['notlive_food_mover_lite_home_monthly'],
      productId: 'prod_notlive_food_mover_lite_home',
      legacyProductIds: ['prod_notlive_delivery_food_runs'],
    },
    live: {
      priceIds: ['notlive_food_mover_lite_home_monthly'],
      productId: 'prod_notlive_food_mover_lite_home',
      legacyProductIds: ['prod_notlive_delivery_food_runs'],
    },
  },
  foodMoverProHome: {
    test: {
      priceIds: ['notlive_food_mover_pro_home_monthly'],
      productId: 'prod_notlive_food_mover_pro_home',
      legacyProductIds: ['prod_notlive_food_mover_home'],
    },
    live: {
      priceIds: ['notlive_food_mover_pro_home_monthly'],
      productId: 'prod_notlive_food_mover_pro_home',
      legacyProductIds: ['prod_notlive_food_mover_home'],
    },
  },
  freshcue: {
    test: {
      priceIds: ['notlive_freshcue'],
      productId: 'prod_notlive_freshcue',
    },
    live: {
      priceIds: ['notlive_freshcue'],
      productId: 'prod_notlive_freshcue',
    },
  },
};

const getSubscriptionPriceIds = (item, environment) =>
  subscriptions[item]?.[environment]?.priceIds ?? [];

const getSubscriptionPriceId = (item, environment) =>
  getSubscriptionPriceIds(item, environment)[0] ?? null;

const getSubscriptionProductId = (item, environment) =>
  subscriptions[item]?.[environment]?.productId ?? null;

const getSubscriptionProductIds = (item, environment) => {
  const productId = getSubscriptionProductId(item, environment);
  return productId ? [productId] : [];
};

const getLegacyProductIds = (item, environment) =>
  subscriptions[item]?.[environment]?.legacyProductIds ?? [];

const getPlanProductIdSet = (item) => [
  ...getSubscriptionProductIds(item, 'test'),
  ...getLegacyProductIds(item, 'test'),
  ...getSubscriptionProductIds(item, 'live'),
  ...getLegacyProductIds(item, 'live'),
];

const getPlanBySubscriptionProductId = (productId) => {
  if (!productId) return null;

  return Object.keys(subscriptions).find((item) =>
    getPlanProductIdSet(item).includes(productId)
  );
};

/** Product IDs for any plan that includes food-run capability. */
const getFoodRunProductIds = () =>
  plansWithCapability('foodRuns').flatMap((key) => getPlanProductIdSet(key));

/** Product IDs for any plan that includes home-delivery capability (incl. legacy bundles). */
const getHomeDeliveryProductIds = () =>
  plansWithCapability('homeDelivery').flatMap((key) =>
    getPlanProductIdSet(key)
  );

/**
 * Stripe product map for the standalone Home Delivery add-on (+$250/mo on Lite/Pro).
 * Not a catalog plan — attached as a second subscription item.
 */
const HOME_DELIVERY_ADDON = {
  test: {
    // $250/mo — Home Delivery add-on (test mode)
    priceIds: ['price_1TwPpaHRNVAUEyHHwgVJoycm'],
    productId: 'prod_UwIQFB8r3nI9Cq',
  },
  live: {
    priceIds: ['price_1TwI0IHRNVAUEyHHjhY10NfI'],
    productId: 'prod_UwAKTSaubiaDMd',
  },
};

const getHomeDeliveryAddonProductId = (environment) =>
  HOME_DELIVERY_ADDON[environment]?.productId ?? null;

const getHomeDeliveryAddonPriceId = (environment) =>
  HOME_DELIVERY_ADDON[environment]?.priceIds?.[0] ?? null;

const getHomeDeliveryAddonProductIds = () =>
  [
    HOME_DELIVERY_ADDON.test.productId,
    HOME_DELIVERY_ADDON.live.productId,
  ].filter(Boolean);

const isHomeDeliveryAddonProductId = (productId) =>
  Boolean(productId) && getHomeDeliveryAddonProductIds().includes(productId);

/** All known HD add-on price IDs (test + live). Used to block HD-only checkout. */
const getHomeDeliveryAddonPriceIds = () =>
  [
    ...(HOME_DELIVERY_ADDON.test?.priceIds || []),
    ...(HOME_DELIVERY_ADDON.live?.priceIds || []),
  ].filter(Boolean);

const isHomeDeliveryAddonPriceId = (priceId) =>
  Boolean(priceId) && getHomeDeliveryAddonPriceIds().includes(priceId);

/**
 * Parse a Stripe Subscription's items into base plan + optional HD add-on.
 * Base plan = first non-addon item that maps to a known plan key.
 */
const parseStripeSubscriptionItems = (subscription) => {
  const items = subscription?.items?.data || [];
  let baseItem = null;
  let addonItem = null;

  for (const item of items) {
    const productId =
      typeof item.price?.product === 'string'
        ? item.price.product
        : item.price?.product?.id;
    if (!productId) continue;

    if (isHomeDeliveryAddonProductId(productId)) {
      addonItem = item;
      continue;
    }
    const planKey = getPlanBySubscriptionProductId(productId);
    if (planKey && !baseItem) {
      baseItem = item;
    } else if (!baseItem) {
      baseItem = item;
    }
  }

  // Fallback: first item if nothing classified
  if (
    !baseItem &&
    items[0] &&
    !isHomeDeliveryAddonProductId(
      typeof items[0].price?.product === 'string'
        ? items[0].price.product
        : items[0].price?.product?.id
    )
  ) {
    baseItem = items[0];
  }

  const baseProductId =
    typeof baseItem?.price?.product === 'string'
      ? baseItem.price.product
      : (baseItem?.price?.product?.id ?? null);

  const rawPlanKey = getPlanBySubscriptionProductId(baseProductId);
  const normalized = normalizePlanKey(rawPlanKey);

  return {
    stripe_product_id: baseProductId,
    stripe_price_id: baseItem?.price?.id ?? null,
    planKey: normalized.planKey,
    home_delivery_addon: Boolean(addonItem) || normalized.homeDeliveryAddon,
    home_delivery_stripe_price_id: addonItem?.price?.id ?? null,
    home_delivery_stripe_subscription_item_id: addonItem?.id ?? null,
  };
};

/**
 * Product IDs for Food Mover Pro-class ops (proOps).
 * Used by authorizeFoodMoverFull / checkIsFoodMover (strict Pro).
 */
const getFoodMoverProProductIds = () =>
  plansWithCapability('proOps').flatMap((key) => getPlanProductIdSet(key));

/**
 * Broad "food mover family" product IDs: food runs and/or home delivery.
 * Used by checkIsFoodMoverCore and similar.
 */
const getFoodMoverProductIds = () => {
  const keys = new Set([
    ...plansWithCapability('foodRuns'),
    ...plansWithCapability('homeDelivery'),
    ...plansWithCapability('proOps'),
  ]);
  return [...keys].flatMap((key) => getPlanProductIdSet(key));
};

const getFoodMoverPriceIds = (environment) => {
  const keys = new Set([
    ...plansWithCapability('foodRuns'),
    ...plansWithCapability('homeDelivery'),
    ...plansWithCapability('proOps'),
  ]);
  return [...keys].flatMap((key) => getSubscriptionPriceIds(key, environment));
};

/**
 * Buyer-facing NPO plan cards (excludes free signup, HD add-on, freshcue).
 */
const NPO_SELF_SERVE_PLAN_KEYS = [
  'foodMoverLite',
  'foodDeliveryPro',
  'foodMover',
];

/**
 * Entries in `subscriptions` that are add-ons, not base plans. They must not be
 * listed as a plan in plan filters or the plan column.
 */
const NPO_ADDON_PLAN_KEYS = ['freshcue'];

/**
 * Admin-grantable base plans (HD is a separate add-on toggle, not a plan key).
 * Legacy *Home keys kept for product-id resolution only.
 */
const NPO_ADMIN_PLAN_KEYS = [
  'basic',
  'foodMoverLite',
  'foodDeliveryPro',
  'foodMover',
];

module.exports = {
  subscriptions,
  getSubscriptionPriceId,
  getSubscriptionPriceIds,
  getSubscriptionProductId,
  getSubscriptionProductIds,
  getPlanBySubscriptionProductId,
  getPlanProductIdSet,
  getFoodMoverPriceIds,
  getFoodMoverProductIds,
  getFoodRunProductIds,
  getHomeDeliveryProductIds,
  getFoodMoverProProductIds,
  HOME_DELIVERY_ADDON,
  getHomeDeliveryAddonProductId,
  getHomeDeliveryAddonPriceId,
  getHomeDeliveryAddonProductIds,
  getHomeDeliveryAddonPriceIds,
  isHomeDeliveryAddonProductId,
  isHomeDeliveryAddonPriceId,
  parseStripeSubscriptionItems,
  NPO_SELF_SERVE_PLAN_KEYS,
  NPO_ADMIN_PLAN_KEYS,
  NPO_ADDON_PLAN_KEYS,
  // capabilities
  PLAN_CAPABILITIES,
  getPlanCapabilities,
  hasPlanCapability,
  plansWithCapability,
  isOpsPlan,
  isFoodMoverLitePlan,
  HOME_DELIVERY_ADDON_ELIGIBLE_PLANS,
  HOME_DELIVERY_ADDON_PLAN_MAP,
  HOME_DELIVERY_ADDON_BASE_PLAN_MAP,
  getHomeDeliveryAddonTargetPlan,
  getHomeDeliveryAddonBasePlan,
  canAddHomeDeliveryAddon,
  isHomeDeliveryAddonPlan,
  normalizePlanKey,
  hasEffectiveHomeDelivery,
  getEffectivePlanCapabilities,
  // display pricing
  PLAN_PRICING,
  HOME_DELIVERY_ADDON_PRICING,
  WHITE_LABEL_PRICING,
  getPlanPricing,
  formatMoney,
  formatPlanPriceSummary,
};
